import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Gift, Sparkles, PartyPopper, Clock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

// Compute an SVG "donut slice" path for a wheel sector
function slicePath(cx, cy, r, startAngle, endAngle) {
  const rad = (a) => (Math.PI / 180) * a;
  const x1 = cx + r * Math.cos(rad(startAngle));
  const y1 = cy + r * Math.sin(rad(startAngle));
  const x2 = cx + r * Math.cos(rad(endAngle));
  const y2 = cy + r * Math.sin(rad(endAngle));
  const large = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
}

export default function WheelPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [prize, setPrize] = useState(null);
  const [showPrize, setShowPrize] = useState(false);
  const [countdown, setCountdown] = useState("");
  const rafRef = useRef(null);
  const spinPromiseRef = useRef(null);
  const stopRequestedRef = useRef(false);

  const load = () => api.get("/wheel/status").then(({ data }) => setStatus(data)).catch(() => {});

  useEffect(() => { if (user) load(); }, [user]);

  // Countdown to next spin
  useEffect(() => {
    if (!status?.next_spin_at) { setCountdown(""); return; }
    const tick = () => {
      const now = new Date();
      const target = new Date(status.next_spin_at);
      const diff = Math.max(0, Math.floor((target - now) / 1000));
      if (diff === 0) { setCountdown(""); load(); return; }
      const h = Math.floor(diff / 3600).toString().padStart(2, "0");
      const m = Math.floor((diff % 3600) / 60).toString().padStart(2, "0");
      const s = (diff % 60).toString().padStart(2, "0");
      setCountdown(`${h}:${m}:${s}`);
    };
    tick();
    const int = setInterval(tick, 1000);
    return () => clearInterval(int);
  }, [status?.next_spin_at]);

  const options = status?.options || [];
  const sliceAngle = options.length ? 360 / options.length : 60;

  const wheelSvg = useMemo(() => {
    if (!options.length) return null;
    const R = 180;
    const CX = 200, CY = 200;
    return (
      <g transform={`rotate(${rotation} ${CX} ${CY})`} style={{ transition: rotating ? "none" : "transform 4.5s cubic-bezier(0.15, 0.8, 0.2, 1)" }}>
        {options.map((o, i) => {
          const start = i * sliceAngle - 90;
          const end = start + sliceAngle;
          const mid = start + sliceAngle / 2;
          const rad = (Math.PI / 180) * mid;
          const tx = CX + (R * 0.62) * Math.cos(rad);
          const ty = CY + (R * 0.62) * Math.sin(rad);
          return (
            <g key={o.option_id}>
              <path d={slicePath(CX, CY, R, start, end)} fill={o.color} stroke="#fff" strokeWidth="3" />
              <text
                x={tx}
                y={ty}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#fff"
                fontSize="14"
                fontWeight="700"
                transform={`rotate(${mid + 90} ${tx} ${ty})`}
                style={{ pointerEvents: "none" }}
              >
                {o.label}
              </text>
            </g>
          );
        })}
      </g>
    );
  }, [options, rotation, rotating, sliceAngle]);

  const startSpin = async () => {
    if (rotating || !status?.can_spin) return;
    setPrize(null);
    setShowPrize(false);
    setRotating(true);
    stopRequestedRef.current = false;

    // Kick off the API call in parallel
    spinPromiseRef.current = api.post("/wheel/spin").then((r) => r.data);
    spinPromiseRef.current.catch((err) => {
      toast.error(formatApiError(err));
      setRotating(false);
      load();
    });

    // Continuous rotation at ~360°/sec until stopRequested + prize known
    let lastTime = performance.now();
    const tick = (t) => {
      const dt = (t - lastTime) / 1000;
      lastTime = t;
      setRotation((r) => r + 360 * dt);
      if (!stopRequestedRef.current) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const stopSpin = async () => {
    if (!rotating) return;
    const spin = await spinPromiseRef.current;
    if (!spin) return;
    // Cancel raf and switch to CSS-transition landing
    stopRequestedRef.current = true;
    cancelAnimationFrame(rafRef.current);

    const winnerIndex = options.findIndex((o) => o.option_id === spin.option_id);
    const targetAngle = -(winnerIndex * sliceAngle + sliceAngle / 2); // pointer at top
    // Add extra full spins for the effect + normalize
    setRotating(false);
    setRotation((current) => {
      const base = ((current % 360) + 360) % 360;
      const extra = 360 * 6; // 6 extra full spins
      const finalAngle = current - base + targetAngle + extra;
      return finalAngle;
    });

    // Reveal prize after transition
    setTimeout(() => {
      setPrize(spin);
      setShowPrize(true);
      // Reload status (may still allow spin if spin_again)
      load();
    }, 4600);
  };

  if (!user) {
    return (
      <div className="je-container py-20 text-center" data-testid="wheel-login-required">
        <Sparkles className="w-12 h-12 text-[#0052CC] mx-auto mb-3" />
        <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>Roue de la chance</h1>
        <p className="text-gray-600 mb-4">Connectez-vous pour tenter votre chance chaque jour !</p>
        <Link to="/login" className="je-btn-primary">Se connecter</Link>
      </div>
    );
  }

  return (
    <div className="je-container py-10" data-testid="wheel-page">
      <Link to="/" className="text-sm text-[#0052CC] inline-flex items-center gap-1 mb-4 hover:underline">
        <ArrowLeft className="w-4 h-4" /> Retour à l&apos;accueil
      </Link>
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 bg-[#E3000F]/10 text-[#E3000F] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-3">
          <Gift className="w-3.5 h-3.5" /> 1 tour gratuit par jour
        </div>
        <h1 className="text-4xl md:text-5xl font-extrabold" style={{ fontFamily: "Outfit" }}>Roue de la Chance</h1>
        <p className="text-gray-500 mt-2">Tournez la roue et remportez des cadeaux, réductions ou une livraison offerte !</p>
      </div>

      <div className="grid md:grid-cols-[1fr_360px] gap-8 items-center">
        {/* Wheel */}
        <div className="relative mx-auto" style={{ width: 420, maxWidth: "100%" }}>
          <svg viewBox="0 0 400 400" className="w-full drop-shadow-2xl" data-testid="wheel-svg">
            <circle cx="200" cy="200" r="188" fill="#0A1430" />
            {wheelSvg}
            <circle cx="200" cy="200" r="30" fill="#fff" stroke="#0A1430" strokeWidth="4" />
            <circle cx="200" cy="200" r="12" fill="#E3000F" />
          </svg>
          {/* Pointer */}
          <div className="absolute left-1/2 -top-2 -translate-x-1/2" style={{ filter: "drop-shadow(0 3px 6px rgba(0,0,0,.35))" }}>
            <svg width="46" height="60" viewBox="0 0 46 60">
              <polygon points="23,52 4,4 42,4" fill="#E3000F" stroke="#fff" strokeWidth="3" />
            </svg>
          </div>
        </div>

        {/* Controls */}
        <div className="space-y-3">
          <div className="bg-white border rounded-xl p-5 text-center">
            {!status?.can_spin && countdown ? (
              <>
                <Clock className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Prochain tour dans :</p>
                <p className="text-3xl font-extrabold" style={{ fontFamily: "Outfit" }} data-testid="wheel-countdown">{countdown}</p>
              </>
            ) : (
              <>
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Prêt à jouer ?</p>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={startSpin}
                    disabled={rotating || !status?.can_spin}
                    className="je-btn-primary text-lg py-3 disabled:opacity-50"
                    data-testid="wheel-start-btn"
                  >
                    {rotating ? "🎡 En rotation…" : "🎯 Tourner la roue"}
                  </button>
                  <button
                    onClick={stopSpin}
                    disabled={!rotating}
                    className="je-btn-blue disabled:opacity-30"
                    data-testid="wheel-stop-btn"
                  >
                    ⏹ STOP
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Recent spins */}
          {status?.recent_spins?.length > 0 && (
            <div className="bg-white border rounded-xl p-4">
              <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-2">Vos derniers gains</p>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {status.recent_spins.slice(0, 8).map((s) => (
                  <div key={s.spin_id} className="flex items-center justify-between text-xs">
                    <span className="font-semibold" style={{ color: s.color }}>{s.label}</span>
                    <span className="text-gray-500">{new Date(s.created_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Prize modal */}
      {showPrize && prize && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="prize-modal" onClick={() => setShowPrize(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-8 text-center relative animate-in zoom-in duration-300" onClick={(e) => e.stopPropagation()}>
            <PartyPopper className="w-14 h-14 text-[#E3000F] mx-auto mb-2" />
            <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Vous avez gagné</p>
            <h2 className="text-3xl font-extrabold mt-1 mb-4" style={{ fontFamily: "Outfit", color: prize.color }}>{prize.label}</h2>
            {prize.type === "nothing" && (
              <p className="text-gray-600">Pas de chance cette fois-ci. Revenez demain, la chance vous sourira !</p>
            )}
            {prize.type === "spin_again" && (
              <>
                <p className="text-gray-600 mb-3">Vous avez droit à un nouveau tour !</p>
                <button onClick={() => { setShowPrize(false); startSpin(); }} className="je-btn-primary" data-testid="spin-again-btn">Rejouer</button>
              </>
            )}
            {prize.promo_code && (
              <>
                <p className="text-gray-600 mb-2">Utilisez ce code au moment du checkout :</p>
                <div className="bg-gradient-to-r from-[#0052CC] to-[#003D99] text-white rounded-lg p-4 font-mono text-2xl font-bold tracking-widest mb-3" data-testid="wheel-promo-code">
                  {prize.promo_code}
                </div>
                <p className="text-xs text-gray-500">À usage unique • Valable sur votre prochaine commande</p>
              </>
            )}
            {prize.type === "product" && (
              <>
                <p className="text-gray-700 mb-3">
                  🎁 Ce cadeau sera <b>automatiquement ajouté gratuitement à votre prochaine commande</b> ! Il apparaîtra en bas de votre panier au moment du checkout.
                </p>
                <Link to="/products" className="je-btn-primary inline-block" data-testid="shop-to-claim-gift">
                  Faire mes achats
                </Link>
              </>
            )}
            <button onClick={() => setShowPrize(false)} className="mt-4 text-sm text-gray-500 hover:text-gray-700">Fermer</button>
          </div>
        </div>
      )}
    </div>
  );
}
